import { z } from 'zod';

// Material participation hours tracking. Geofency posts arrive and leave events
// for saved locations; the location name decides which company gets the hours.
// Not company-scoped: every row belongs to the signed-in owner (Entra oid).

export const MP_EVENT_TYPES = ['arrive', 'leave', 'unknown'] as const;
export const mpEventTypeSchema = z.enum(MP_EVENT_TYPES);
export type MpEventType = z.infer<typeof mpEventTypeSchema>;

export const MP_ADAPTERS = ['geofency', 'generic'] as const;
export type MpAdapter = (typeof MP_ADAPTERS)[number];

export const MP_DEFAULT_MIN_DWELL_MINUTES = 3;

/** Estimated travel, in minutes. Ten hours is far past any real round trip. */
const travelMinutes = z.number().int().min(0).max(600);

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v ? v : null));

export const createMpPlaceSchema = z.object({
  companyId: z.string().uuid(),
  name: z.string().trim().min(1).max(120),
  /** The exact location name Geofency sends. Matched without regard to case. */
  externalPlaceName: z.string().trim().min(1).max(200),
  address: optionalText(300),
  minDwellMinutes: z.number().int().min(0).max(240).default(MP_DEFAULT_MIN_DWELL_MINUTES),
  /** Estimated round trip to this place. Credited once per place per day. */
  travelMinutes: travelMinutes.default(0),
  defaultNote: optionalText(500),
  defaultWorkCategory: optionalText(120),
});
export type CreateMpPlaceInput = z.input<typeof createMpPlaceSchema>;

export const updateMpPlaceSchema = z.object({
  companyId: z.string().uuid().optional(),
  name: z.string().trim().min(1).max(120).optional(),
  externalPlaceName: z.string().trim().min(1).max(200).optional(),
  address: optionalText(300).optional(),
  minDwellMinutes: z.number().int().min(0).max(240).optional(),
  travelMinutes: travelMinutes.optional(),
  defaultNote: optionalText(500).optional(),
  defaultWorkCategory: optionalText(120).optional(),
  isActive: z.boolean().optional(),
});
export type UpdateMpPlaceInput = z.input<typeof updateMpPlaceSchema>;

export interface MpPlace {
  id: string;
  companyId: string;
  companyName: string;
  name: string;
  externalPlaceName: string;
  address: string | null;
  minDwellMinutes: number;
  travelMinutes: number;
  defaultNote: string | null;
  defaultWorkCategory: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export const createMpWebhookTokenSchema = z.object({
  label: z.string().trim().min(1).max(80).default('Geofency'),
});
export type CreateMpWebhookTokenInput = z.input<typeof createMpWebhookTokenSchema>;

/** A token as the settings page sees it. The secret itself is never stored. */
export interface MpWebhookToken {
  id: string;
  label: string;
  /** Last four characters, so two tokens can be told apart. */
  hint: string;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

/** Returned exactly once, when the token is generated. */
export interface MpWebhookTokenCreated {
  token: MpWebhookToken;
  secret: string;
}

export interface MpRawEvent {
  id: string;
  sequenceNumber: number;
  receivedAt: string;
  deviceTime: string | null;
  deviceTimeRaw: string | null;
  eventType: MpEventType;
  externalPlaceName: string | null;
  placeId: string | null;
  placeName: string | null;
  latitude: string | null;
  longitude: string | null;
  deviceId: string | null;
  adapter: string;
  contentType: string | null;
  rawPayload: string;
  isDuplicate: boolean;
  /** Fired from the sender's test button. Stored, never counted. */
  isTest: boolean;
  rowHash: string;
}

export interface MpChainProblem {
  sequence: number;
  problem: 'hash_mismatch' | 'broken_link' | 'sequence_gap';
}

export interface MpChainVerifyResult {
  valid: boolean;
  count: number;
  headHash: string | null;
  problems: MpChainProblem[];
}

// ── Thresholds ─────────────────────────────────────────────────────────────

export const mpThresholdsSchema = z.object({
  /** IANA zone that decides which calendar day and tax year a session falls in. */
  timeZone: z.string().min(1).max(64).default('America/Chicago'),
  /** A session open longer than this is treated as a missed leave. */
  maxSessionHours: z.number().int().min(1).max(48).default(16),
  /** Phone and server clocks further apart than this flag the session. */
  clockSkewMinutes: z.number().int().min(1).max(240).default(10),
});
export type MpThresholds = z.infer<typeof mpThresholdsSchema>;
export const MP_DEFAULT_THRESHOLDS: MpThresholds = mpThresholdsSchema.parse({});

export const updateMpThresholdsSchema = mpThresholdsSchema.partial();
export type UpdateMpThresholdsInput = z.input<typeof updateMpThresholdsSchema>;

// ── Sessions ───────────────────────────────────────────────────────────────

// open               arrived, not yet left
// confirmed          counts toward hours (set automatically on a clean leave)
// excluded_short     shorter than the place's minimum stay; kept, never counted
// excluded_personal  marked "not work" by hand; kept, never counted
// exception          could not be paired cleanly; not counted until fixed
export const MP_SESSION_STATUSES = [
  'open',
  'confirmed',
  'excluded_short',
  'excluded_personal',
  'exception',
] as const;
export type MpSessionStatus = (typeof MP_SESSION_STATUSES)[number];

export const MP_EXCEPTION_TYPES = [
  'leave_without_arrive',
  'arrive_while_open',
  'overlap',
  'clock_skew',
  'missed_leave',
] as const;
export type MpExceptionType = (typeof MP_EXCEPTION_TYPES)[number];

export const MP_PARTICIPANTS = ['self', 'spouse'] as const;
export const mpParticipantSchema = z.enum(MP_PARTICIPANTS);
export type MpParticipant = z.infer<typeof mpParticipantSchema>;

export const MP_SESSION_SOURCES = ['auto', 'manual'] as const;
export type MpSessionSource = (typeof MP_SESSION_SOURCES)[number];

export interface MpSession {
  id: string;
  placeId: string | null;
  placeName: string | null;
  placeAddress: string | null;
  companyId: string;
  companyName: string;
  participant: MpParticipant;
  startAt: string | null;
  endAt: string | null;
  durationMinutes: number | null;
  /** Estimated travel credited to this visit. Separate from durationMinutes. */
  travelMinutes: number;
  note: string | null;
  workCategory: string | null;
  source: MpSessionSource;
  status: MpSessionStatus;
  exceptionType: MpExceptionType | null;
  isEdited: boolean;
  createdAt: string;
}

export interface MpSessionEdit {
  id: string;
  sessionId: string;
  field: string;
  oldValue: string | null;
  newValue: string | null;
  reason: string | null;
  editedAt: string;
}

const instant = z.string().datetime({ offset: true });

export const createMpManualSessionSchema = z.object({
  companyId: z.string().uuid(),
  placeId: z.string().uuid().nullish(),
  participant: mpParticipantSchema.default('self'),
  startAt: instant,
  endAt: instant,
  travelMinutes: travelMinutes.default(0),
  note: z.string().trim().min(1).max(500),
  workCategory: optionalText(120),
});
export type CreateMpManualSessionInput = z.input<typeof createMpManualSessionSchema>;

/** Statuses a person may set by hand. open and exception come only from events. */
export const mpResolvableStatusSchema = z.enum(['confirmed', 'excluded_personal']);

export const updateMpSessionSchema = z.object({
  startAt: instant.optional(),
  endAt: instant.optional(),
  companyId: z.string().uuid().optional(),
  participant: mpParticipantSchema.optional(),
  travelMinutes: travelMinutes.optional(),
  note: optionalText(500).optional(),
  workCategory: optionalText(120).optional(),
  status: mpResolvableStatusSchema.optional(),
  /** Required whenever a time changes. */
  reason: z.string().trim().max(300).optional(),
});
export type UpdateMpSessionInput = z.input<typeof updateMpSessionSchema>;

export const mpSessionsQuerySchema = z.object({
  from: instant.optional(),
  to: instant.optional(),
  companyId: z.string().uuid().optional(),
});
export type MpSessionsQuery = z.infer<typeof mpSessionsQuerySchema>;

// ── Year lock ──────────────────────────────────────────────────────────────

/** Sessions in this tax year and every earlier one are read only. */
export interface MpYearLock {
  lockedThroughYear: number | null;
}

const taxYear = z.number().int().min(2000).max(2100);

export const lockMpYearSchema = z.object({ year: taxYear });
export type LockMpYearInput = z.infer<typeof lockMpYearSchema>;

/** Reopens `year` and every year after it. The reason is written to the audit log. */
export const unlockMpYearSchema = z.object({
  year: taxYear,
  reason: z.string().trim().min(3).max(300),
});
export type UnlockMpYearInput = z.infer<typeof unlockMpYearSchema>;

// ── Hours report ───────────────────────────────────────────────────────────

export const mpReportQuerySchema = z.object({
  year: z.coerce.number().int().min(2000).max(2100),
});
export const mpReportExportQuerySchema = mpReportQuerySchema.extend({
  format: z.enum(['xlsx', 'pdf']),
});
export type MpReportExportQuery = z.infer<typeof mpReportExportQuerySchema>;

/**
 * Confirmed time for one business in one tax year. Minutes are whole numbers.
 *
 * totalMinutes and everything split out of it (self, spouse, auto, manual) is
 * ON-SITE time only. Travel is an estimate, so it is carried beside the on-site
 * figures and never folded into them; whoever reads the report decides whether
 * it counts.
 */
export interface MpHoursRow {
  companyId: string;
  companyName: string;
  selfMinutes: number;
  spouseMinutes: number;
  autoMinutes: number;
  manualMinutes: number;
  totalMinutes: number;
  /** Two decimals, as a string, like every other reported figure in the app. */
  totalHours: string;
  /** Straight-line pace to December 31. Null for a year that is over. */
  projectedHours: string | null;
  over100: boolean;
  over500: boolean;
  travelMinutes: number;
  travelHours: string;
  /** On-site plus estimated travel. */
  withTravelHours: string;
  over100WithTravel: boolean;
  over500WithTravel: boolean;
  sessionCount: number;
}

/** One confirmed session, or one calendar day of a session that crossed midnight. */
export interface MpLogRow {
  sessionId: string;
  date: string;
  start: string;
  end: string;
  minutes: number;
  hours: string;
  /** Estimated travel, shown on the first day of the visit only. */
  travelMinutes: number;
  travelHours: string;
  placeName: string | null;
  placeAddress: string | null;
  companyId: string;
  companyName: string;
  participant: MpParticipant;
  workCategory: string | null;
  note: string | null;
  source: MpSessionSource;
  isEdited: boolean;
  attachmentCount: number;
}

export interface MpNotCountedRow {
  sessionId: string;
  date: string | null;
  start: string | null;
  end: string | null;
  minutes: number | null;
  placeName: string | null;
  companyName: string;
  status: MpSessionStatus;
  exceptionType: MpExceptionType | null;
  /** Plain-language reason the time is not in the totals. */
  reason: string;
}

export interface MpEditRow {
  editedAt: string;
  sessionId: string;
  sessionDate: string | null;
  companyName: string;
  field: string;
  oldValue: string | null;
  newValue: string | null;
  reason: string | null;
}

export interface MpReport {
  year: number;
  timeZone: string;
  generatedAt: string;
  isCurrentYear: boolean;
  daysElapsed: number;
  daysInYear: number;
  lockedThroughYear: number | null;
  rows: MpHoursRow[];
  totalMinutes: number;
  totalHours: string;
  travelMinutes: number;
  travelHours: string;
  withTravelHours: string;
  log: MpLogRow[];
  notCounted: MpNotCountedRow[];
  edits: MpEditRow[];
  chain: MpChainVerifyResult & { eventsInYear: number };
}
